import { cn } from "@/lib/utils";
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";
import type { ComponentProps } from "react";
import { Drawer as DrawerPrimitive } from "vaul";
import Check from "../icons/check";
import ChevronRight from "../icons/chevron-right";
import Indicator from "../icons/indicator";

export default function DrawerMenu({
  ...props
}: ComponentProps<typeof DrawerPrimitive.Root>) {
  return <DrawerPrimitive.Root data-slot="drawer-menu" {...props} />;
}

export function DrawerMenuPortal({
  ...props
}: ComponentProps<typeof DrawerPrimitive.Portal>) {
  return <DrawerPrimitive.Portal data-slot="drawer-menu-portal" {...props} />;
}

export function DrawerMenuTrigger({
  className,
  ...props
}: ComponentProps<typeof DrawerPrimitive.Trigger>) {
  return (
    <DrawerPrimitive.Trigger
      data-slot="drawer-menu-trigger"
      className={cn(
        "interactive has-icon has-icon-size-4 cursor-pointer",
        className,
      )}
      {...props}
    />
  );
}

export function DrawerMenuContent({
  className,
  children,
  ...props
}: ComponentProps<typeof DrawerPrimitive.Content>) {
  return (
    <DrawerPrimitive.Portal>
      <DrawerPrimitive.Overlay className="data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/50" />
      <DrawerPrimitive.Content
        data-slot="drawer-menu-content"
        className={cn(
          "group/drawer-menu-content bg-background-neutral border-border text-foreground fixed inset-x-0 bottom-0 z-50 mt-24 flex max-h-[80vh] flex-col gap-1 rounded-t-lg border border-t shadow-lg",
          className,
        )}
        {...props}
      >
        <div className="relative isolate flex flex-1 flex-col gap-1 overflow-y-auto px-4 pt-12 pb-6">
          <div className="bg-background-neutral-disabled absolute top-5 left-1/2 mx-auto h-1.5 w-12 shrink-0 -translate-x-1/2 rounded-full" />
          {children}
        </div>
      </DrawerPrimitive.Content>
    </DrawerPrimitive.Portal>
  );
}

export function DrawerMenuGroup({ ...props }: ComponentProps<"div">) {
  return <div data-slot="drawer-menu-group" {...props} />;
}

export function DrawerMenuItem({
  inset,
  variant = "default",
  className,
  ...props
}: ComponentProps<typeof DrawerPrimitive.Close> & {
  inset?: boolean;
  variant?: "default" | "critical";
}) {
  return (
    <DrawerPrimitive.Close
      data-slot="drawer-menu-item"
      data-inset={inset}
      data-variant={variant}
      className={cn(
        "interactive hover:bg-foreground/20 active:bg-foreground/30 data-[variant=critical]:text-foreground-critical data-[variant=critical]:hover:bg-background-critical-disabled/50 data-[variant=critical]:active:bg-background-critical-disabled/70 has-icon has-icon-size-4 relative flex w-full cursor-pointer items-center gap-2 rounded-sm px-4 py-3 text-sm outline-hidden select-none data-inset:pl-6 md:data-inset:pl-10",
        className,
      )}
      {...props}
    />
  );
}

export function DrawerMenuCheckboxItem({
  checked,
  children,
  className,
  onCheckedChange,
  ...props
}: ComponentProps<"button"> & {
  checked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
}) {
  return (
    <button
      type="button"
      data-slot="drawer-menu-checkbox-item"
      className={cn(
        "interactive hover:bg-foreground/20 active:bg-foreground/30 has-icon has-icon-size-4 relative flex w-full cursor-pointer items-center gap-2 rounded-sm px-4 py-3 text-sm",
        className,
      )}
      onClick={() => onCheckedChange?.(!checked)}
      {...props}
    >
      {checked ? <Check className="size-4" /> : <span className="size-4" />}
      {children}
    </button>
  );
}

export function DrawerMenuRadioGroup(
  props: ComponentProps<typeof RadioGroupPrimitive.Root>,
) {
  return (
    <RadioGroupPrimitive.Root data-slot="drawer-menu-radio-group" {...props} />
  );
}

export function DrawerMenuRadioItem({
  children,
  className,
  ...props
}: ComponentProps<typeof RadioGroupPrimitive.Item>) {
  return (
    <RadioGroupPrimitive.Item
      data-slot="drawer-menu-radio-item"
      className={cn(
        "interactive hover:bg-foreground/20 active:bg-foreground/30 has-icon has-icon-size-4 relative flex w-full cursor-pointer items-center gap-2 rounded-sm px-4 py-3 text-sm",
        className,
      )}
      {...props}
    >
      <span className="size-4">
        <RadioGroupPrimitive.Indicator>
          <Indicator />
        </RadioGroupPrimitive.Indicator>
      </span>
      {children}
    </RadioGroupPrimitive.Item>
  );
}

export function DrawerMenuLabel({
  className,
  inset,
  ...props
}: ComponentProps<"div"> & {
  inset?: boolean;
}) {
  return (
    <div
      data-slot="drawer-menu-label"
      data-inset={inset}
      className={cn(
        "text-foreground-pressed/70 px-4 py-2 text-xs data-inset:pl-6 md:data-inset:pl-10",
        className,
      )}
      {...props}
    />
  );
}

export function DrawerMenuSeparator({
  className,
  ...props
}: ComponentProps<"div">) {
  return (
    <div
      data-slot="drawer-menu-separator"
      className={cn("bg-border -mx-1 my-1 h-px", className)}
      {...props}
    />
  );
}

export function DrawerMenuShortcut({
  className,
  ...props
}: ComponentProps<"span">) {
  return (
    <span
      data-slot="drawer-menu-shortcut"
      className={cn(
        "text-foreground ml-auto text-xs tracking-widest",
        className,
      )}
      {...props}
    />
  );
}

export function DrawerMenuSub({
  ...props
}: ComponentProps<typeof DrawerPrimitive.Root>) {
  return <DrawerPrimitive.Root data-slot="drawer-menu-sub" {...props} />;
}

export function DrawerMenuSubTrigger({
  className,
  inset,
  children,
  ...props
}: ComponentProps<typeof DrawerPrimitive.Trigger> & {
  inset?: boolean;
}) {
  return (
    <DrawerPrimitive.Trigger
      data-slot="drawer-menu-sub-trigger"
      data-inset={inset}
      className={cn(
        "interactive hover:bg-foreground/20 active:bg-foreground/30 data-[state=open]:bg-foreground/20 data-[state=open]:text-foreground has-icon has-icon-size-4 relative flex cursor-pointer items-center gap-2 rounded-sm px-4 py-3 text-sm outline-hidden select-none data-inset:pl-6 md:data-inset:pl-10",
        className,
      )}
      {...props}
    >
      {children}
      <ChevronRight className="ml-auto size-4" />
    </DrawerPrimitive.Trigger>
  );
}

export function DrawerMenuSubContent({
  className,
  children,
  ...props
}: ComponentProps<typeof DrawerPrimitive.Content>) {
  return (
    <DrawerPrimitive.Portal>
      <DrawerPrimitive.Overlay className="data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/50" />
      <DrawerPrimitive.Content
        data-slot="drawer-menu-sub-content"
        className={cn(
          "group/drawer-menu-sub-content bg-background-neutral border-border text-foreground fixed inset-x-0 bottom-0 z-50 mt-24 flex max-h-[80vh] flex-col gap-1 rounded-t-lg border border-t shadow-lg",
          className,
        )}
        {...props}
      >
        <div className="relative isolate flex flex-1 flex-col gap-1 overflow-y-auto px-4 pt-12 pb-6">
          <div className="bg-background-neutral-disabled absolute top-5 left-1/2 mx-auto h-1.5 w-12 shrink-0 -translate-x-1/2 rounded-full" />
          {children}
        </div>
      </DrawerPrimitive.Content>
    </DrawerPrimitive.Portal>
  );
}
